import { useState } from 'react'
import { useAppStore } from '../../store/appStore'
import { useGoalsStore } from '../Goals/useGoalsStore'
import { useAuthStore } from '../../auth/useAuthStore'

export type ActivityView = 'files' | 'search' | 'git' | 'goals' | 'extensions' | 'collab' | 'shared' | 'pulse-memory' | 'packages' | 'secrets' | 'database' | 'templates' | 'deploy' | 'versions' | 'community' | 'comments' | 'billing' | 'cron' | 'analytics' | 'themes' | 'teams' | 'review' | 'docs' | 'scanner' | 'cicd' | 'marketplace' | 'debugger' | 'enterprise' | 'components' | 'compare' | 'research' | 'privacy' | 'permissions' | 'storage' | 'automations' | 'canvas' | 'seo' | 'validation' | 'figma' | 'artifacts' | 'agent-goals'

interface ActivityBarProps {
  activeView: ActivityView
  onViewChange: (v: ActivityView) => void
  isPreviewOpen: boolean
  onTogglePreview: () => void
  isTerminalOpen: boolean
  onToggleTerminal: () => void
}

// Core items always visible
const CORE_ITEMS: { id: ActivityView; icon: string; label: string }[] = [
  { id: 'files',      icon: '☰', label: 'Explorer' },
  { id: 'search',     icon: '⌕', label: 'Search' },
  { id: 'git',        icon: '⎇',  label: 'Source Control' },
  { id: 'pulse-memory', icon: 'P', label: 'Pulse Memory' },
  { id: 'templates',  icon: '⚡', label: 'Generate App' },
  { id: 'extensions', icon: '◐', label: 'Visual Builder' },
]

// Tools section
const TOOL_ITEMS: { id: ActivityView; icon: string; label: string }[] = [
  { id: 'components',  icon: '⬚', label: 'Components' },
  { id: 'packages',    icon: '⊞', label: 'Packages' },
  { id: 'database',    icon: '⛁', label: 'Database' },
  { id: 'deploy',      icon: '↗', label: 'Deploy' },
  { id: 'storage',     icon: '📁', label: 'Storage' },
  { id: 'canvas',      icon: '🎨', label: 'Canvas' },
  { id: 'agent-goals', icon: '🤖', label: 'AQ Agent' },
  { id: 'cicd',        icon: '⚙', label: 'CI/CD' },
  { id: 'debugger',    icon: '⚑', label: 'Debugger' },
]

// Social/collab section
const SOCIAL_ITEMS: { id: ActivityView; icon: string; label: string }[] = [
  { id: 'shared',      icon: '🔗', label: 'Shared' },
  { id: 'teams',       icon: '⁂', label: 'Teams' },
  { id: 'community',   icon: '◎', label: 'Explore' },
  { id: 'marketplace', icon: '⊕', label: 'Extensions' },
]

// More menu items (less frequently used)
const MORE_ITEMS: { id: ActivityView; icon: string; label: string }[] = [
  { id: 'secrets',     icon: '⚿', label: 'Secrets' },
  { id: 'versions',    icon: '⟲', label: 'Version History' },
  { id: 'comments',    icon: '⌸', label: 'Comments' },
  { id: 'cron',        icon: '⏱', label: 'Cron Jobs' },
  { id: 'automations', icon: '⚡', label: 'Automations' },
  { id: 'themes',      icon: '◐', label: 'Themes' },
  { id: 'analytics',   icon: '⊟', label: 'Analytics' },
  { id: 'seo',          icon: '📈', label: 'SEO' },
  { id: 'validation',  icon: '🧪', label: 'Tests' },
  { id: 'figma',       icon: '🎨', label: 'Figma Import' },
  { id: 'artifacts',   icon: '📦', label: 'Artifacts' },
  { id: 'billing',     icon: '□', label: 'Billing' },
  { id: 'review',      icon: '⌕', label: 'Code Review' },
  { id: 'docs',        icon: '☷', label: 'Generate Docs' },
  { id: 'scanner',     icon: '⛨', label: 'Security Scanner' },
  { id: 'compare',    icon: '⚔', label: 'Model Compare' },
  { id: 'goals',       icon: '◎', label: 'Goals & Roadmap' },
  { id: 'privacy',     icon: '🛡', label: 'Privacy' },
  { id: 'permissions', icon: '🔐', label: 'Permissions' },
  { id: 'enterprise',  icon: '⚐', label: 'Platform Admin' },
]

export function ActivityBar({ activeView, onViewChange, isPreviewOpen, onTogglePreview, isTerminalOpen, onToggleTerminal }: ActivityBarProps) {
  const { toggleSidebar, isSidebarOpen } = useAppStore()
  const userEmail = useAuthStore(s => s.user?.email)
  const userRole = useAuthStore(s => s.user?.role)
  const userPlan = useAuthStore(s => s.user?.plan)
  const isAdmin = userRole === 'admin' && userPlan !== 'enterprise'
  const [moreOpen, setMoreOpen] = useState(false)
  const [expanded, setExpanded] = useState(() => localStorage.getItem('pulse-sidebar-expanded') === 'true')

  const toggleExpanded = () => {
    const next = !expanded
    setExpanded(next)
    localStorage.setItem('pulse-sidebar-expanded', String(next))
  }

  const handleClick = (id: ActivityView) => {
    if (id === 'goals') {
      onViewChange(activeView === 'goals' ? 'files' : 'goals')
      return
    }
    if (id === 'templates') {
      window.dispatchEvent(new CustomEvent('pulse:open-generate'))
      return
    }
    if (id === 'extensions' && CORE_ITEMS.find(i => i.id === 'extensions')?.label === 'Visual Builder') {
      window.dispatchEvent(new CustomEvent('pulse:open-builder'))
      return
    }
    if (activeView === id && isSidebarOpen) {
      toggleSidebar()
    } else {
      onViewChange(id)
      if (!isSidebarOpen) toggleSidebar()
    }
    setMoreOpen(false)
  }

  return (
    <div className="activity-bar" style={{ width: expanded ? 160 : undefined, alignItems: expanded ? 'stretch' : undefined }}>
      {/* Expand/collapse toggle */}
      <button className="activity-bar__btn" title={expanded ? 'Collapse' : 'Expand'} onClick={toggleExpanded} style={{ fontSize: 12 }}>
        {expanded ? '◂' : '▸'}
      </button>

      <div style={{ width: expanded ? '80%' : 24, height: 1, background: 'var(--border)', margin: expanded ? '4px auto' : '4px auto' }} />

      {/* Core */}
      {CORE_ITEMS.map(item => (
        <button key={item.id} className={`activity-bar__btn ${activeView === item.id ? 'activity-bar__btn--active' : ''}`} title={!expanded ? item.label : undefined} onClick={() => handleClick(item.id)} style={expanded ? { justifyContent: 'flex-start', width: '100%', gap: 8, padding: '6px 10px', borderRadius: 6 } : undefined}>
          <span style={{ fontSize: 14, width: 20, textAlign: 'center', flexShrink: 0 }}>{item.icon}</span>
          {expanded && <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>}
        </button>
      ))}

      <div style={{ width: expanded ? '80%' : 24, height: 1, background: 'var(--border)', margin: '4px auto' }} />

      {/* Tools */}
      {TOOL_ITEMS.map(item => (
        <button key={item.id} className={`activity-bar__btn ${activeView === item.id ? 'activity-bar__btn--active' : ''}`} title={!expanded ? item.label : undefined} onClick={() => handleClick(item.id)} style={expanded ? { justifyContent: 'flex-start', width: '100%', gap: 8, padding: '6px 10px', borderRadius: 6 } : undefined}>
          <span style={{ fontSize: 14, width: 20, textAlign: 'center', flexShrink: 0 }}>{item.icon}</span>
          {expanded && <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>}
        </button>
      ))}

      <div style={{ width: expanded ? '80%' : 24, height: 1, background: 'var(--border)', margin: '4px auto' }} />

      {/* Social */}
      {SOCIAL_ITEMS.map(item => (
        <button key={item.id} className={`activity-bar__btn ${activeView === item.id ? 'activity-bar__btn--active' : ''}`} title={!expanded ? item.label : undefined} onClick={() => handleClick(item.id)} style={expanded ? { justifyContent: 'flex-start', width: '100%', gap: 8, padding: '6px 10px', borderRadius: 6 } : undefined}>
          <span style={{ fontSize: 14, width: 20, textAlign: 'center', flexShrink: 0 }}>{item.icon}</span>
          {expanded && <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>}
        </button>
      ))}

      <div style={{ width: expanded ? '80%' : 24, height: 1, background: 'var(--border)', margin: '4px auto' }} />

      {/* More menu */}
      {!expanded && (
      <div style={{ position: 'relative' }}>
        <button className={`activity-bar__btn ${moreOpen ? 'activity-bar__btn--active' : ''}`} title={!expanded ? 'More' : undefined} onClick={() => setMoreOpen(!moreOpen)} style={expanded ? { justifyContent: 'flex-start', width: '100%', gap: 8, padding: '6px 10px', borderRadius: 6 } : undefined}>
          <span style={{ fontSize: 14, width: 20, textAlign: `center`, flexShrink: 0 }}>⋯</span>
          {expanded && <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)' }}>More</span>}
        </button>
        {moreOpen && (
          <div style={{
            position: 'absolute', left: '100%', bottom: 0, marginLeft: 4,
            background: 'var(--bg-1)', border: '1px solid var(--border-2)', borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)', padding: '6px 0', zIndex: 100,
            minWidth: 180, maxHeight: 320, overflowY: 'auto',
          }}>
            {MORE_ITEMS.filter(item => item.id !== 'enterprise' || isAdmin).map(item => (
              <button key={item.id} onClick={() => handleClick(item.id)} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 14px', background: activeView === item.id ? 'rgba(124,110,245,0.1)' : 'none',
                border: 'none', cursor: 'pointer', color: activeView === item.id ? 'var(--accent)' : 'var(--text-1)',
                fontFamily: 'var(--font-mono)', fontSize: 11, textAlign: 'left', transition: 'background 0.1s',
              }} onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-3)' }}
                 onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = activeView === item.id ? 'rgba(124,110,245,0.1)' : 'none' }}>
                <span style={{ fontSize: 14, width: 20, textAlign: 'center' }}>{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Show all MORE items inline when expanded */}
      {expanded && (
        <>
          <div style={{ width: '80%', height: 1, background: 'var(--border)', margin: '4px auto' }} />
          {MORE_ITEMS.filter(item => item.id !== 'enterprise' || isAdmin).map(item => (
            <button key={item.id} className={`activity-bar__btn ${activeView === item.id ? 'activity-bar__btn--active' : ''}`} onClick={() => handleClick(item.id)} style={{ justifyContent: 'flex-start', width: '100%', gap: 8, padding: '6px 10px', borderRadius: 6 }}>
              <span style={{ fontSize: 14, width: 20, textAlign: 'center', flexShrink: 0 }}>{item.icon}</span>
              <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
            </button>
          ))}
        </>
      )}

      <div style={{ flex: 1 }} />
      <button
        className={`activity-bar__btn ${isPreviewOpen ? 'activity-bar__btn--active' : ''}`}
        title={!expanded ? 'Preview' : undefined}
        onClick={onTogglePreview}
        style={expanded ? { justifyContent: 'flex-start', width: '100%', gap: 8, padding: '6px 10px', borderRadius: 6 } : undefined}
      >
        <span style={{ fontSize: 14, width: 20, textAlign: 'center', flexShrink: 0 }}>{'▶'}</span>
        {expanded && <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)' }}>Preview</span>}
      </button>
      <button
        className={`activity-bar__btn ${isTerminalOpen ? 'activity-bar__btn--active' : ''}`}
        title={!expanded ? 'Terminal' : undefined}
        onClick={onToggleTerminal}
        style={expanded ? { justifyContent: 'flex-start', width: '100%', gap: 8, padding: '6px 10px', borderRadius: 6 } : undefined}
      >
        <span style={{ fontSize: 14, width: 20, textAlign: 'center', flexShrink: 0 }}>{'⌨'}</span>
        {expanded && <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)' }}>Terminal</span>}
      </button>
    </div>
  )
}

function GoalsBadge() {
  const pct = useGoalsStore((s) => s.progressPct())
  if (pct === 0) return null
  return (
    <span style={{
      position: 'absolute', bottom: 3, right: 3, fontSize: 7,
      fontFamily: 'var(--font-mono)',
      color: pct === 100 ? 'var(--accent-2)' : 'var(--accent)',
      lineHeight: 1, fontWeight: 700,
    }}>
      {pct}
    </span>
  )
}
